import calculateGridDateFromPoint from '../calculation/calculateGridDateFromPoint';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import { action } from 'satcheljs/lib/legacy';
import { makeDragHelperItemId } from '../utils/dragItemUtils';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import {
    SurfaceDragDropConfig,
    DroppableType,
    DragOperationType,
    CalendarSurfaceStore,
    ColumnProvider,
    SurfaceDragDropState,
} from 'owa-calendar-surface-dategrid';
import {
    addDays,
    addMilliseconds,
    addMinutes,
    compare,
    differenceInDays,
    differenceInMilliseconds,
    getDate,
    getHours,
    getMinutes,
    getMonth,
    getTimestamp,
    getYear,
    isAfter,
    isBefore,
    OwaDate,
    startOfDay,
    userDate,
} from 'owa-datetime';

/**
 * Starts or updates a drag operation
 * @param surfaceStore The surface store
 * @param dragDropConfig drag and drop config
 * @param droppableElement The html element for the droppable region over which we are dragging
 * @param droppableType The type of droppable, i.e., day/week, all-day
 * @param dragPositionX: The page x position where we are dragging
 * @param dragPositionY: The page y position where we are dragging
 */
export let startOrUpdateDragOperation = action('startOrUpdateDragOperation')(
    function startOrUpdateDragOperation(
        surfaceStore: CalendarSurfaceStore,
        dragDropConfig: SurfaceDragDropConfig,
        columnProvider: ColumnProvider,
        droppableElement: HTMLElement,
        droppableType: DroppableType,
        dragPositionX: number,
        dragPositionY: number
    ): void {
        let { dragDropState } = surfaceStore;
        let dragDate: OwaDate = calculateGridDateFromPoint(
            surfaceStore,
            columnProvider,
            droppableElement,
            droppableType,
            dragPositionX,
            dragPositionY
        );

        if (dragDropState.helperItemModel == null) {
            startDragOperation(
                surfaceStore,
                dragDropConfig,
                columnProvider,
                droppableElement,
                droppableType,
                dragDate,
                dragPositionX,
                dragPositionY
            );
            return;
        }

        dragDropState.dragPosition.x = dragPositionX;
        dragDropState.dragPosition.y = dragPositionY;
        dragDropState.droppableType = droppableType;
        updateHelperItem(dragDropState, dragDate, surfaceStore.userOptions.hourIncrement);
    }
);

function startDragOperation(
    surfaceStore: CalendarSurfaceStore,
    dragDropConfig: SurfaceDragDropConfig,
    columnProvider: ColumnProvider,
    droppableElement: HTMLElement,
    droppableType: DroppableType,
    dragDate: OwaDate,
    dragPositionX: number = null,
    dragPositionY: number = null
): void {
    let { dragDropState } = surfaceStore;

    dragDropState.droppableType = droppableType;
    dragDropState.dragPosition =
        isNaN(dragPositionX) || isNaN(dragPositionY)
            ? null
            : { x: dragPositionX, y: dragPositionY };

    initializeDragOffset(surfaceStore, columnProvider, droppableElement, droppableType);
    initializeHelperItem(dragDropState, dragDate, surfaceStore.userOptions.hourIncrement);

    // Notify consumer that dragging is starting
    dragDropConfig.onDragStart(dragDropState.draggedItemModel);
}

function initializeDragOffset(
    surfaceStore: CalendarSurfaceStore,
    columnProvider: ColumnProvider,
    droppableElement: HTMLElement,
    droppableType: DroppableType
) {
    let { dragDropState } = surfaceStore;
    let { dragStartPosition } = surfaceStore.dragDropState;
    let dragOffsetDate = calculateGridDateFromPoint(
        surfaceStore,
        columnProvider,
        droppableElement,
        droppableType,
        dragStartPosition.x,
        dragStartPosition.y
    );

    // Account for multi-day events of custom duration
    let draggedItemModel = dragDropState.draggedItemModel;
    let originalStart = draggedItemModel.Start;
    if (droppableType != DroppableType.DayWeek) {
        let offsetMinutes =
            getHours(originalStart) * TimeConstants.MinutesInOneHour + getMinutes(originalStart);
        dragOffsetDate = addMinutes(dragOffsetDate, offsetMinutes);
    }

    dragDropState.dragOffset = getTimestamp(dragOffsetDate) - getTimestamp(draggedItemModel.Start);
    dragDropState.dragStartDate = startOfDay(dragOffsetDate);
}

function initializeHelperItem(
    dragDropState: SurfaceDragDropState,
    dragDate: OwaDate,
    timeIncrementInMinutes: number
): void {
    let { draggedItemModel } = dragDropState;

    let itemId = { ...draggedItemModel.ItemId };
    itemId.Id = makeDragHelperItemId(itemId.Id);
    let helperItem = { ...draggedItemModel };
    helperItem.ItemId = { ...itemId };
    dragDropState.helperItemModel = helperItem;
    updateHelperItem(dragDropState, dragDate, timeIncrementInMinutes);
}

function updateHelperItem(
    dragDropState: SurfaceDragDropState,
    dragDate: OwaDate,
    timeIncrementInMinutes: number
): void {
    if (dragDropState.dragType == DragOperationType.Drag) {
        updateHelperItemForDrag(dragDropState, dragDate);
    } else {
        updateHelperItemForResize(dragDropState, dragDate, timeIncrementInMinutes);
    }
}

function updateHelperItemForDrag(dragDropState: SurfaceDragDropState, dragDate: OwaDate) {
    let { draggedItemModel, helperItemModel, droppableType, dragOffset } = dragDropState;
    let duration = differenceInMilliseconds(draggedItemModel.End, draggedItemModel.Start);
    let durationInDays = differenceInDays(draggedItemModel.End, draggedItemModel.Start);

    // Determine which draggable was the original, in case of month view it would be itself
    let originalDroppable = null;
    if (dragDropState.droppableType == DroppableType.Month) {
        originalDroppable = DroppableType.Month;
    } else {
        originalDroppable =
            duration >= TimeConstants.MillisecondsInOneDay
                ? DroppableType.AllDay
                : DroppableType.DayWeek;
    }

    if (droppableType == originalDroppable) {
        // Dragging within time grid or within all day well
        let { draggedItemModel, helperItemModel } = dragDropState;
        let newStart: OwaDate;

        if (droppableType == DroppableType.DayWeek) {
            newStart = dragDate;
        } else {
            // For all day and month events, we need to preserve the time part
            let originalStart = draggedItemModel.Start;
            newStart = userDate(
                getYear(dragDate),
                getMonth(dragDate),
                getDate(dragDate),
                getHours(originalStart),
                getMinutes(originalStart)
            );
        }

        newStart = draggedItemModel.IsAllDayEvent
            ? addDays(newStart, -dragOffset / TimeConstants.MillisecondsInOneDay)
            : addMilliseconds(newStart, -dragOffset);
        updateStartTime(helperItemModel, newStart);

        const newEnd = draggedItemModel.IsAllDayEvent
            ? addDays(helperItemModel.Start, durationInDays)
            : addMilliseconds(helperItemModel.Start, duration);
        updateEndTime(helperItemModel, newEnd);

        helperItemModel.IsAllDayEvent = draggedItemModel.IsAllDayEvent;
        return;
    }

    updateStartTime(helperItemModel, dragDate);
    if (droppableType == DroppableType.AllDay) {
        // Dragging from time grid to all day well
        updateEndTime(helperItemModel, addDays(dragDate, 1));
        helperItemModel.IsAllDayEvent = true;
    } else {
        // Dragging from all day well to time grid
        updateEndTime(
            helperItemModel,
            addMinutes(dragDate, SurfaceConstants.DefaultDurationInMinutes)
        );
        helperItemModel.IsAllDayEvent = false;
    }
}

function updateHelperItemForResize(
    dragDropState: SurfaceDragDropState,
    dragDate: OwaDate,
    timeIncrementInMinutes: number
) {
    let { draggedItemModel, helperItemModel, dragType } = dragDropState;
    let endDateTime: OwaDate;

    switch (dragType) {
        case DragOperationType.ResizeTop:
            if (isAfter(dragDate, draggedItemModel.End)) {
                // Make 0 duration
                updateStartTime(helperItemModel, draggedItemModel.End);
            } else {
                updateStartTime(helperItemModel, dragDate);
            }
            break;

        case DragOperationType.ResizeBottom:
            dragDate = addMinutes(dragDate, timeIncrementInMinutes);
            if (isBefore(dragDate, draggedItemModel.Start)) {
                // Make 0 duration
                updateEndTime(helperItemModel, draggedItemModel.Start);
            } else {
                updateEndTime(helperItemModel, dragDate);
            }
            break;

        case DragOperationType.ResizeLeft:
            let newStart: OwaDate;
            endDateTime = draggedItemModel.End;
            if (isBefore(dragDate, endDateTime)) {
                newStart = dragDate;
            } else {
                // Calculate the new start date as follows:
                // 1. If event ends at midnight boundary, set start to be the previous day
                // 2. else event ends at a custom time, i.e. something like 11/3 at 4pm, in
                //    which case we set start to the same day as start of end.
                newStart = computeDateForResize(endDateTime, endDateTime, -1);
            }

            // Respect original start time to properly handle custom multi-day events
            updateStartTime(
                helperItemModel,
                userDate(
                    getYear(newStart),
                    getMonth(newStart),
                    getDate(newStart),
                    getHours(helperItemModel.Start),
                    getMinutes(helperItemModel.Start)
                )
            );
            break;

        case DragOperationType.ResizeRight:
            endDateTime = draggedItemModel.End;
            let newEnd: OwaDate;
            let beginOfStart = startOfDay(draggedItemModel.Start);
            if (isBefore(dragDate, beginOfStart)) {
                // Calculate the new end date as follows:
                // 1. If event ends at midnight boundary, set end to be the next day
                // 2. else event ends at a custom time, i.e. something like 11/3 at 4pm, in
                //    which case we set end to the start date. Time will be added at the end.
                newEnd = computeDateForResize(endDateTime, beginOfStart, 1);
            } else {
                // Calculate the new end date as follows:
                // 1. If event ends at midnight boundary, set end to be the end of the drag date (+1 day)
                // 2. else event ends at a custom time, i.e. something like 11/3 at 4pm, in which case
                //    we set end to the drag date itself. Time will be added at the end.
                newEnd = computeDateForResize(endDateTime, dragDate, 1);
            }

            // Respect original end time to properly handle custom multi-day events
            updateEndTime(
                helperItemModel,
                userDate(
                    getYear(newEnd),
                    getMonth(newEnd),
                    getDate(newEnd),
                    getHours(helperItemModel.End),
                    getMinutes(helperItemModel.End)
                )
            );
            break;
    }
}

function computeDateForResize(endDateTime: OwaDate, baseDate: OwaDate, daysToAdd: number) {
    let startOfEnd = startOfDay(endDateTime);
    if (compare(startOfEnd, endDateTime) == 0) {
        return addDays(baseDate, daysToAdd);
    } else {
        return baseDate;
    }
}

function updateStartTime(helperItemModel: CalendarEvent, newStartTime: OwaDate): void {
    if (compare(newStartTime, helperItemModel.Start) !== 0) {
        helperItemModel.Start = newStartTime;
    }
}

function updateEndTime(helperItemModel: CalendarEvent, newEndTime: OwaDate): void {
    if (compare(newEndTime, helperItemModel.End) !== 0) {
        helperItemModel.End = newEndTime;
    }
}
